import { MenuNavLink } from '@/app/components/MenuBar/MenuNavLink';
import { EnvironmentBadge } from '@/app/components/Sidebar/EnvironmentBadge';
import { useMenuGroups } from '@/app/navigation/Sidebar';
import { logoutUser, useAuth } from '@/features/users';
import type { AppDispatch, RootState } from '@/store';
import { cilAccountLogout, cilMenu, cilUser } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import { CBadge, CButton } from '@coreui/react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

export const MenuBar: React.FC = () => {
  const mobilePanelRef = useRef<HTMLDivElement | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileGroupOpen, setMobileGroupOpen] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const menuGroups = useMenuGroups();
  const visibleGroups = menuGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.show || item.show()),
    }))
    .filter((group) => group.items.length > 0);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuth();
  const user = useSelector((state: RootState) => state.users.currentUser);

  const userFullName =
    `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || '';
  const userRole = user?.roles?.[0]?.name || '-';
  const userLabel = userFullName ? `${userFullName} - ${userRole}` : '';
  const activeMobileGroupLabel =
    visibleGroups.find((group) => group.items.some((item) => pathname.startsWith(item.to)))
      ?.label || null;

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      localStorage.removeItem('currentUser');
      localStorage.removeItem('accessToken');
      void navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const closeMobileMenu = () => {
    setMobileOpen(false);
    setMobileGroupOpen(null);
  };

  const toggleMobileMenu = () => {
    setMobileOpen((open) => {
      const nextOpen = !open;

      if (nextOpen) {
        setMobileGroupOpen(activeMobileGroupLabel);
      }

      return nextOpen;
    });
  };

  const handleUserDropdownBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setUserDropdownOpen(false);
    }
  };

  useEffect(() => {
    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target as Element;

      if (
        target.closest('.menu-bar__dropdown') ||
        target.closest('.menu-bar__direct-link') ||
        target.closest('.menu-bar__user') ||
        target.closest('.menu-bar__hamburger') ||
        mobilePanelRef.current?.contains(target)
      ) {
        return;
      }

      setOpenDropdown(null);
      setUserDropdownOpen(false);
      setMobileOpen(false);
      setMobileGroupOpen(null);
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, []);

  return (
    <header className="menu-bar">
      <div className="menu-bar__inner">
        <div className="menu-bar__brand">
          <img src="/untamed.svg" alt="Untamed" className="menu-bar__logo" />
          <EnvironmentBadge />
        </div>

        <nav className="menu-bar__desktop-nav" aria-label="Main navigation">
          {visibleGroups.map((group) => {
            if (group.label === 'Customers') {
              const customerItem = group.items[0];
              return (
                <MenuNavLink
                  key={group.label}
                  label={group.label}
                  to={customerItem.to}
                  className="menu-bar__direct-link"
                />
              );
            }

            return (
              <div
                className={`menu-bar__dropdown ${openDropdown === group.label ? 'is-open' : ''}`}
                key={group.label}
                onMouseEnter={() => setOpenDropdown(group.label)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button
                  className="menu-bar__dropdown-toggle"
                  type="button"
                  onFocus={() => setOpenDropdown(group.label)}
                >
                  {group.label}
                </button>
                <div className="menu-bar__dropdown-menu">
                  {group.items.map((item) => (
                    <MenuNavLink
                      key={item.to}
                      label={item.label}
                      to={item.to}
                      onNavigate={() => setOpenDropdown(null)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        {isAuthenticated && user && (
          <div
            className={`menu-bar__user menu-bar__desktop-user ${userDropdownOpen ? 'is-open' : ''}`}
            onMouseEnter={() => setUserDropdownOpen(true)}
            onMouseLeave={() => setUserDropdownOpen(false)}
            onBlur={handleUserDropdownBlur}
          >
            <button
              className="menu-bar__dropdown-toggle menu-bar__user-toggle"
              type="button"
              aria-label="User profile"
              onFocus={() => setUserDropdownOpen(true)}
            >
              <span className="menu-bar__user-label">{userLabel}</span>
              <CIcon icon={cilUser} className="menu-bar__user-icon" />
            </button>
            <div className="menu-bar__dropdown-menu menu-bar__dropdown-menu--right">
              <div className="menu-bar__user-summary">
                <div className="menu-bar__user-name">{userFullName || user.email}</div>
                <div className="menu-bar__user-role">({userRole})</div>
              </div>
              <button
                className="menu-bar__dropdown-action"
                type="button"
                onClick={(...args: Parameters<typeof handleLogout>) => void handleLogout(...args)}
              >
                <CIcon icon={cilAccountLogout} className="me-2" />
                Logout
              </button>
            </div>
          </div>
        )}

        <CButton
          color="dark"
          variant="ghost"
          className="menu-bar__hamburger"
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation"
          aria-expanded={mobileOpen}
        >
          <CIcon icon={cilMenu} />
        </CButton>
      </div>

      {mobileOpen && (
        <div className="menu-bar__mobile-panel" ref={mobilePanelRef}>
          {visibleGroups.map((group) => {
            if (group.label === 'Customers') {
              const customerItem = group.items[0];
              return (
                <MenuNavLink
                  key={group.label}
                  label={group.label}
                  to={customerItem.to}
                  className="menu-bar__mobile-group-toggle"
                  onNavigate={closeMobileMenu}
                />
              );
            }

            const isOpen = mobileGroupOpen === group.label;
            return (
              <div className="menu-bar__mobile-group" key={group.label}>
                <button
                  className="menu-bar__mobile-group-toggle"
                  type="button"
                  onClick={() => setMobileGroupOpen(isOpen ? null : group.label)}
                  aria-expanded={isOpen}
                >
                  {group.label}
                </button>
                {isOpen && (
                  <div className="menu-bar__mobile-links">
                    {group.items.map((item) => (
                      <MenuNavLink
                        key={item.to}
                        label={item.label}
                        to={item.to}
                        className="menu-bar__mobile-link"
                        onNavigate={closeMobileMenu}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {isAuthenticated && user && (
            <div className="menu-bar__mobile-user">
              <div className="menu-bar__mobile-user-name">{userFullName || user.email}</div>
              <CBadge color="info">{userRole}</CBadge>
              <CButton
                color="danger"
                variant="ghost"
                size="sm"
                onClick={(...args: Parameters<typeof handleLogout>) => void handleLogout(...args)}
              >
                <CIcon icon={cilAccountLogout} className="me-2" />
                Logout
              </CButton>
            </div>
          )}
        </div>
      )}
    </header>
  );
};
